import { useEffect } from "react";
import { BRAND } from "./brand";
import { appConfig } from "@/config/app";
import { pageMetadata } from "@/config/seo";

/**
 * Titre d'onglet et description (moteurs de recherche) d'une page du site.
 * En quittant le site (connexion, app), l'onglet reprend le nom de l'instance.
 */
export function usePageMeta(title: string, description: string, path = window.location.pathname) {
  useEffect(() => {
    const page = pageMetadata(path, BRAND, appConfig.domain);
    const restore: Array<() => void> = [];
    function meta(key: string, value: string | undefined, property = false) {
      const attribute = property ? "property" : "name";
      const previous = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      const oldValue = previous?.getAttribute("content");
      if (value === undefined) {
        if (previous) {
          previous.remove();
          restore.push(() => document.head.append(previous));
        }
        return;
      }
      const element = previous ?? document.createElement("meta");
      element.setAttribute(attribute, key);
      element.content = value;
      if (!previous) document.head.append(element);
      restore.push(() => {
        if (!previous) element.remove();
        else if (oldValue !== null && oldValue !== undefined) element.content = oldValue;
        else element.removeAttribute("content");
      });
    }
    document.title = title;
    meta("description", description);
    meta("robots", page.robots);
    meta("og:type", "website", true);
    meta("og:locale", "fr_FR", true);
    meta("og:site_name", BRAND, true);
    meta("og:title", title, true);
    meta("og:description", description, true);
    meta("og:url", page.canonical, true);
    meta("twitter:card", "summary");
    meta("twitter:title", title);
    meta("twitter:description", description);

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const oldHref = canonical?.getAttribute("href");
    const link = canonical ?? document.createElement("link");
    if (page.canonical) {
      link.rel = "canonical";
      link.href = page.canonical;
      if (!canonical) document.head.append(link);
    } else canonical?.remove();
    restore.push(() => {
      if (!canonical) link.remove();
      else {
        if (oldHref !== null && oldHref !== undefined) canonical.setAttribute("href", oldHref);
        else canonical.removeAttribute("href");
        if (!canonical.isConnected) document.head.append(canonical);
      }
    });
    return () => {
      restore.reverse().forEach((reset) => reset());
      document.title = BRAND;
    };
  }, [title, description, path]);
}
